import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import { formatMoney, convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { userDisplayNameAsString } from '../../util/data';
import {
  getSupportedProcessesInfo,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ModalInMobile, PrimaryButton, AvatarSmall, H1, H2 } from '../../components';

import ProductOrderForm from './ProductOrderForm/ProductOrderForm';
import css from './OrderPanel.module.css';

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const { Money } = sdkTypes;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    campaignPromoStrikeThroughPrice = null,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);

  // Check if there's an active sale (supports number 1, boolean true, or string "true")
  const sales = publicData?.sales;
  const isOnSale = sales === 1 || sales === true || sales === 'true' || sales === '1';
  const originalPrice = publicData?.original_price;

  // Format original price if sale is active AND new price is strictly lower than original price
  let formattedOriginalPrice = null;
  let formattedDiscountAmount = null;
  if (isOnSale && originalPrice && price) {
    try {
      const { Money } = sdkTypes;
      // Convert original_price from main units to subunits (e.g., 19.99 EUR -> 1999 cents)
      const subUnitDivisor = unitDivisor(price.currency);
      const originalPriceInSubunits = convertUnitToSubUnit(originalPrice, subUnitDivisor);
      const originalAmount = originalPriceInSubunits;
      const currentAmount = price.amount;

      // Only show strikethrough price if new price is strictly lower than original price
      if (originalAmount > currentAmount) {
        const originalPriceMoney = new Money(originalPriceInSubunits, price.currency);
        formattedOriginalPrice = formatMoney(intl, originalPriceMoney);
        const discountMoney = new Money(originalAmount - currentAmount, price.currency);
        formattedDiscountAmount = formatMoney(intl, discountMoney);
      }
    } catch (error) {
      // Silently handle error
    }
  }

  // Signup campaign: prix catalogue barré + pastille montant — prioritaire sur la mise en forme "soldes" marchand
  let useCampaignPromoUi = false;
  let formattedCampaignOriginalPrice = null;
  let formattedCampaignDiscountAmount = null;
  if (
    campaignPromoStrikeThroughPrice &&
    price &&
    campaignPromoStrikeThroughPrice.currency === price.currency &&
    campaignPromoStrikeThroughPrice.amount > price.amount
  ) {
    try {
      formattedCampaignOriginalPrice = formatMoney(intl, campaignPromoStrikeThroughPrice);
      const orig = campaignPromoStrikeThroughPrice.amount;
      const cur = price.amount;
      formattedCampaignDiscountAmount = formatMoney(intl, new Money(orig - cur, price.currency));
      useCampaignPromoUi = true;
    } catch (e) {
      useCampaignPromoUi = false;
    }
  }

  const showSaleUi =
    useCampaignPromoUi || (!useCampaignPromoUi && isOnSale && formattedOriginalPrice);
  const strikeThroughFormatted = useCampaignPromoUi
    ? formattedCampaignOriginalPrice
    : formattedOriginalPrice;
  const badgeDiscountFormatted = useCampaignPromoUi
    ? formattedCampaignDiscountAmount
    : formattedDiscountAmount;

  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        {showSaleUi && strikeThroughFormatted ? (
          <div className={css.priceWithSaleInCTA}>
            <div className={css.priceSaleStrikethroughRow}>
              {badgeDiscountFormatted ? (
                <span className={css.discountBadgeInCTA}>-{badgeDiscountFormatted}</span>
              ) : null}
              <span className={css.originalPriceInCTA}>{strikeThroughFormatted}</span>
            </div>
            {priceValue}
          </div>
        ) : (
          priceValue
        )}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      {showSaleUi && strikeThroughFormatted ? (
        <div className={css.priceWithSale}>
          <p className={css.price}>
            {priceValue} {pricePerUnit}
          </p>
          <div className={css.originalPriceWrapper}>
            {badgeDiscountFormatted ? (
              <span className={css.discountBadge}>-{badgeDiscountFormatted}</span>
            ) : null}
            <span className={css.originalPrice}>{strikeThroughFormatted}</span>
          </div>
        </div>
      ) : (
        <p className={css.price}>
          {priceValue} {pricePerUnit}
        </p>
      )}
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Function} [props.onOpenSellerPanel] - Handler for opening the seller side panel
 * @param {Function} [props.onOpenLoyaltyPanel] - Handler for opening the loyalty side panel
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    onOpenSellerPanel,
    onOpenLoyaltyPanel,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    campaignPromoStrikeThroughPrice,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '' } = publicData || {};

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const showPriceMissing = !price;
  const showInvalidCurrency = price?.currency !== marketplaceCurrency;

  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isPurchase = isPurchaseProcess(processName);
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  const showProductOrderForm = shouldHavePurchase && !isClosed && typeof currentStock === 'number';
  // Stock relationship not ready yet — reserve column space (no layout jump).
  const showFormPlaceholder = shouldHavePurchase && !isClosed && typeof currentStock !== 'number';

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const displayShipping =
    listingType === 'sell-new-products' ? true : displayDeliveryShipping(listingTypeConfig);
  const displayPickup =
    listingType === 'sell-new-products' ? true : displayDeliveryPickup(listingTypeConfig);

  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        {showListingImage && (
          <div className={css.orderHeading}>
            {price && (
              <H2 className={css.priceTitle}>
                <PriceMaybe
                  price={price}
                  publicData={publicData}
                  validListingTypes={validListingTypes}
                  intl={intl}
                  marketplaceCurrency={marketplaceCurrency}
                  campaignPromoStrikeThroughPrice={campaignPromoStrikeThroughPrice}
                />
              </H2>
            )}
            {subTitleText ? <p className={css.orderHelp}>{subTitleText}</p> : null}
          </div>
        )}

        {/* <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
        /> */}

        {/* <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
          </span>
          <span className={css.providerNamePlain}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
          </span>
        </div> */}

        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={onContactUser}
            onOpenSellerPanel={onOpenSellerPanel}
            onOpenLoyaltyPanel={onOpenLoyaltyPanel}
            listing={listing}
            reviews={props.reviews || []}
            {...sharedProps}
          />
        ) : showFormPlaceholder ? (
          <div className={css.productOrderFormPlaceholder} aria-hidden="true" />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
          campaignPromoStrikeThroughPrice={campaignPromoStrikeThroughPrice}
        />

        <button
          className={
            isClosed
              ? css.closedListingButton
              : isOutOfStock
              ? `${css.ctaButton} ${css.ctaButtonOutOfStock}`
              : css.ctaButton
          }
          onClick={
            isClosed
              ? undefined
              : isPurchase
              ? () => {
                  if (isOwnListing || isClosed) {
                    window.scrollTo(0, 0);
                    return;
                  }
                  const defaultDeliveryMethod =
                    (shippingEnabled && displayShipping) || displayShipping
                      ? 'shipping'
                      : pickupEnabled && displayPickup
                      ? 'pickup'
                      : 'shipping';
                  onSubmit({ quantity: '1', deliveryMethod: defaultDeliveryMethod });
                }
              : handleSubmit(isOwnListing, isClosed, false, onSubmit, history, location)
          }
          disabled={isOutOfStock || isClosed}
        >
          {isClosed ? (
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          ) : isOutOfStock ? (
            <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
          ) : (
            <FormattedMessage id="OrderPanel.ctaButtonMessageAddToCart" />
          )}
        </button>
      </div>
    </div>
  );
};

export default OrderPanel;
